import React, { useState } from "react";
import { FeatureRequest } from "interfaces/feature-request";
import styled from "styled-components";
import styles from "../../../styles/styles";
import DescriptionTab from "./DescriptionTab";
import SolutionTab from "./SolutionTab";
import UpdatesTab from "./UpdatesTab";
import moment from "moment";
import DetailsTab from "./DetailsTab";
import { useTranslate } from "hooks/common/useTranslate/useTranslate";
import Tag, { STATE_CONFIG } from "../shared/Tag";
import CommentsTab from "./CommentsTab";
import DecisionTab from "./DecisionTab";

const HeaderContainer = styled.div`
    padding-top: 3rem;
`;

const Header = styled.h4`
    margin-top: 0;
    margin-bottom: 0;
    font-weight: 400;
    font-size: 32px;
`;

const HeaderInfo = styled.div`
    font-size: 16px;
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
`;

const CloseButton = styled.button`
    margin-bottom: 2rem;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 18px;
    line-height: 1;
    padding: 0;
    color: ${styles.colors.red};
    display: flex;
    align-items: center;
    gap: ${styles.spacing[1]};

    &:before {
        content: "←";
        font-size: 20px;
    }

    &:hover {
        color: ${styles.colors.darkRed};
    }
`;

const TabContainer = styled.div`
    margin-top: 1rem;
`;

const TabList = styled.div`
    display: flex;
`;

const IdField = styled.span`
    margin-right: 0.5rem;
`;

const Tab = styled.button<{ active: boolean }>`
    background: none;
    border: none;
    padding: 1rem 1.5rem;
    cursor: pointer;
    font-size: 16px;
    font-weight: ${(props) => (props.active ? "500" : "400")};
    color: ${(props) => (props.active ? styles.colors.grey8 : styles.colors.grey6)};
    border-bottom: 2px solid ${(props) => (props.active ? styles.colors.grey6 : "transparent")};
    margin-bottom: -2px;

    &:hover {
        color: ${styles.colors.grey8};
    }
`;

const TabContent = styled.div`
    padding: 1.5rem;
    border: 2px solid ${styles.colors.grey4};
`;

const DetailsPage: React.FC<{
    featureRequest: FeatureRequest;
    onBack: () => void;
}> = ({ featureRequest, onBack }) => {
    const [activeTab, setActiveTab] = useState("description");
    const translate = useTranslate();

    const commentCount = featureRequest.updates.reduce(
        (acc, update) => acc + 1 + update.replies.length,
        0
    );

    const tabs = [
        { id: "description", label: translate("featureRequests.featureRequestPage.description") },
        {
            id: "solution",
            label: translate("featureRequests.featureRequestPage.solutionProposal"),
        },
        {
            id: "decision",
            label: translate("featureRequests.featureRequestPage.decision"),
        },
        { id: "details", label: translate("featureRequests.featureRequestPage.additionalInfo") },
        { id: "updates", label: translate("featureRequests.featureRequestPage.updateHistory") },
        {
            id: "comments",
            label: `${translate("featureRequests.featureRequestPage.comments")} (${commentCount})`,
        },
    ].filter((tab) => tab.id !== "decision" || !!featureRequest.decision);

    const renderTabContent = () => {
        switch (activeTab) {
            case "description":
                return <DescriptionTab featureRequest={featureRequest} />;
            case "solution":
                return <SolutionTab featureRequest={featureRequest} />;
            case "details":
                return <DetailsTab featureRequest={featureRequest} />;
            case "updates":
                return <UpdatesTab featureRequest={featureRequest} />;
            case "comments":
                return <CommentsTab featureRequest={featureRequest} />;
            case "decision":
                return <DecisionTab featureRequest={featureRequest} />;
            default:
                return null;
        }
    };

    return (
        <>
            <HeaderContainer>
                <CloseButton
                    onClick={onBack}
                    aria-label={translate("featureRequests.featureRequestPage.closeWindow")}
                >
                    {translate("featureRequests.featureRequestPage.back")}
                </CloseButton>
                <HeaderInfo>
                    {featureRequest.featureRequestId && (
                        <IdField>#{featureRequest.featureRequestId}</IdField>
                    )}
                    <Tag {...STATE_CONFIG[featureRequest.state]} />
                </HeaderInfo>
                <Header>{featureRequest.name}</Header>
                <HeaderInfo>
                    {translate("featureRequests.featureRequestPage.updated")}{" "}
                    {moment(featureRequest.updatedAt).format("DD.MM.YYYY")}
                </HeaderInfo>
            </HeaderContainer>

            <TabContainer>
                <TabList>
                    {tabs.map((tab) => (
                        <Tab
                            key={tab.id}
                            active={activeTab === tab.id}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.label}
                        </Tab>
                    ))}
                </TabList>
                <TabContent>{renderTabContent()}</TabContent>
            </TabContainer>
        </>
    );
};

export default DetailsPage;
